import { useEffect, useState } from 'react';

import {
  ChakraBox,
  ChakraFlex,
  ChakraImage,
  ChakraText,
  CircularProgress,
  Close,
  Collapse,
  EquipUIFireflyV2Theme,
  EquipUITheme,
  ExpandAlt,
  Minimize,
  Modal,
} from '@equip.health/ui';

import { useNoteAttachmentModalContext } from '~/lib/contexts/NoteAttachmentModalContext';
import useS3FileDownload from '~/lib/hooks/useS3FileDownload';
import fileUtil from '~/lib/util/file.util';

const { h6 } = EquipUITheme.typography;

const {
  neutral: { secondary: neutralSecondary },
} = EquipUIFireflyV2Theme.colors;

const iconProps: {
  fill: string;
  height: string;
  width: string;
} = {
  fill: neutralSecondary,
  height: '20px',
  width: '20px',
};

const buttonProps: {
  height: string;
  padding: string;
  role: string;
  width: string;
} = {
  height: '40px',
  padding: '10px',
  role: 'button',
  width: '40px',
};

const AttachmentModal = () => {
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [isImageLoaded, setIsImageLoaded] = useState(false);

  const { showNoteAttachmentModal, setShowNoteAttachmentModal } =
    useNoteAttachmentModalContext();

  const { name, url } = showNoteAttachmentModal ?? {};

  const {
    data: attachmentDownloadData,
    error: attachmentDownloadError,
    isFileDownloaded,
    loading: isAttachmentDownloading,
    sendRequest: sendAttachmentDownloadRequest,
  } = useS3FileDownload();

  useEffect(() => {
    if (isFileDownloaded && attachmentDownloadData && showNoteAttachmentModal) {
      const blobURL = window.URL.createObjectURL(
        new Blob([attachmentDownloadData]),
      );
      const link = document.createElement('a');
      link.href = blobURL;
      link.setAttribute('download', name);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }, [isFileDownloaded]);

  useEffect(() => {
    if (attachmentDownloadError && showNoteAttachmentModal) {
      window.open(url, '_blank', 'noopener');
    }
  }, [attachmentDownloadError]);

  useEffect(() => {
    if (!showNoteAttachmentModal) {
      setIsFullScreen(false);
      setIsImageLoaded(false);
    }
  }, [showNoteAttachmentModal]);

  const viewHeight = isFullScreen ? 'calc(100vh - 140px)' : '65vh';

  return (
    <Modal
      footerContent={null}
      hideCloseButton
      id="attachment"
      isOpen={Boolean(showNoteAttachmentModal)}
      onClose={() => setShowNoteAttachmentModal(null)}
      size={isFullScreen ? 'full' : '5xl'}
      title={
        <ChakraFlex justifyContent="space-between" width="100%">
          <ChakraText {...h6} lineHeight="40px">
            {name ?? ''}
          </ChakraText>
          <ChakraFlex>
            {fileUtil.isImageFile(name) && (
              <>
                {isAttachmentDownloading ? (
                  <ChakraBox {...buttonProps}>
                    <CircularProgress
                      id="attachment-download"
                      isIndeterminate
                      size="20px"
                    />
                  </ChakraBox>
                ) : (
                  <ChakraBox
                    {...buttonProps}
                    marginRight="8px"
                    onClick={() => {
                      sendAttachmentDownloadRequest({ url });
                    }}
                  >
                    <Minimize {...iconProps} />
                  </ChakraBox>
                )}
              </>
            )}
            <ChakraBox
              {...buttonProps}
              marginRight="8px"
              onClick={() => {
                setIsFullScreen(!isFullScreen);
              }}
            >
              {isFullScreen ? (
                <Collapse {...iconProps} />
              ) : (
                <ExpandAlt {...iconProps} />
              )}
            </ChakraBox>
            <ChakraBox
              {...buttonProps}
              onClick={() => setShowNoteAttachmentModal(null)}
            >
              <Close {...iconProps} />
            </ChakraBox>
          </ChakraFlex>
        </ChakraFlex>
      }
    >
      <ChakraBox>
        <iframe id="my_iframe" style={{ display: 'none' }} title={name} />

        {fileUtil.isImageFile(name) ? (
          <ChakraBox position="relative">
            <ChakraImage
              alt={name}
              objectFit="contain"
              onLoad={() => setIsImageLoaded(true)}
              src={url}
              style={{ height: viewHeight }}
              visibility={isImageLoaded ? 'visible' : 'hidden'}
              width="100%"
            />
            {!isImageLoaded && (
              <ChakraFlex
                alignItems="center"
                bottom={0}
                justifyContent="center"
                left={0}
                position="absolute"
                right={0}
                top={0}
              >
                <CircularProgress
                  id="image-loading"
                  isIndeterminate
                  size="30px"
                />
              </ChakraFlex>
            )}
          </ChakraBox>
        ) : (
          <iframe
            src={url}
            style={{ height: viewHeight }}
            title={name}
            width="100%"
          />
        )}
      </ChakraBox>
    </Modal>
  );
};

export default AttachmentModal;
